import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Wind, Timer, Zap, Moon, Sun, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import NeonButton from "@/components/NeonButton";

const breathworkExercises = [
  {
    id: "box",
    name: "Box Breathing",
    icon: "◻️",
    description: "4-4-4-4 pattern for calm & focus",
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
    cycles: 4,
    color: "#7C3AED",
    benefits: ["Reduces stress", "Improves focus", "Balances nervous system"]
  },
  {
    id: "478",
    name: "4-7-8 Sleep",
    icon: "🌙",
    description: "Natural tranquilizer for sleep",
    inhale: 4,
    hold1: 7,
    exhale: 8,
    hold2: 0,
    cycles: 4,
    color: "#6366F1",
    benefits: ["Promotes sleep", "Reduces anxiety", "Slows heart rate"]
  },
  {
    id: "energize",
    name: "Energizing Breath",
    icon: "⚡",
    description: "Quick energy boost",
    inhale: 2,
    hold1: 0,
    exhale: 2,
    hold2: 0,
    cycles: 10,
    color: "#F59E0B",
    benefits: ["Increases energy", "Boosts alertness", "Wakes you up"]
  },
  {
    id: "calm",
    name: "Calming Breath",
    icon: "🧘",
    description: "Extended exhale for relaxation",
    inhale: 4,
    hold1: 2,
    exhale: 6,
    hold2: 2,
    cycles: 6,
    color: "#06B6D4",
    benefits: ["Deep relaxation", "Activates parasympathetic", "Reduces tension"]
  },
  {
    id: "wim",
    name: "Wim Hof",
    icon: "❄️",
    description: "Power breathing technique",
    inhale: 2,
    hold1: 0,
    exhale: 2,
    hold2: 0,
    cycles: 30,
    color: "#3B82F6",
    benefits: ["Boosts immunity", "Increases oxygen", "Mental clarity"]
  },
  {
    id: "coherent",
    name: "Coherent",
    icon: "💜",
    description: "5-5 heart coherence pattern",
    inhale: 5,
    hold1: 0,
    exhale: 5,
    hold2: 0,
    cycles: 6,
    color: "#EC4899",
    benefits: ["Heart-brain sync", "Emotional balance", "Stress resilience"]
  }
];

const phases = ["inhale", "hold1", "exhale", "hold2"];
const phaseLabels = {
  inhale: "Breathe In",
  hold1: "Hold",
  exhale: "Breathe Out",
  hold2: "Hold"
};

const Breathwork = ({ audioContext, binauralActive }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState("inhale");
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [circleScale, setCircleScale] = useState(1);
  
  const timerRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);

  const playTone = useCallback((frequency, duration) => {
    if (!audioContext) return;
    
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0, audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
    gain.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration);
    
    osc.connect(gain);
    gain.connect(audioContext.destination);
    osc.start();
    osc.stop(audioContext.currentTime + duration);
  }, [audioContext]);

  const startExercise = useCallback(() => {
    if (!selectedExercise) return;
    
    setIsRunning(true);
    setCurrentCycle(1);
    setCurrentPhase("inhale");
    setTimeLeft(selectedExercise.inhale);
    setCircleScale(1);
    
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    playTone(440, 0.3);
  }, [selectedExercise, audioContext, playTone]);

  const stopExercise = useCallback(() => {
    setIsRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setCircleScale(1);
  }, []);

  const getNextPhase = useCallback((exercise, phase, cycle) => {
    const currentIndex = phases.indexOf(phase);
    let nextIndex = (currentIndex + 1) % 4;
    
    // Skip phases with 0 duration
    while (exercise[phases[nextIndex]] === 0) {
      nextIndex = (nextIndex + 1) % 4;
      if (nextIndex === 0) {
        // Completed a full cycle
        if (cycle >= exercise.cycles) {
          return { phase: null, cycle: cycle };
        }
        return { phase: phases[nextIndex], cycle: cycle + 1 };
      }
    }
    
    if (nextIndex === 0 && phase !== "inhale") {
      if (cycle >= exercise.cycles) {
        return { phase: null, cycle: cycle };
      }
      return { phase: phases[nextIndex], cycle: cycle + 1 };
    }
    
    return { phase: phases[nextIndex], cycle };
  }, []);

  useEffect(() => {
    if (!isRunning || !selectedExercise) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0.1) {
          const { phase: nextPhase, cycle: nextCycle } = getNextPhase(
            selectedExercise, 
            currentPhase, 
            currentCycle
          );
          
          if (nextPhase === null) {
            stopExercise();
            playTone(880, 0.5);
            return 0;
          }
          
          setCurrentPhase(nextPhase);
          setCurrentCycle(nextCycle);
          
          // Play transition tone
          if (nextPhase === 'inhale') playTone(523, 0.2);
          else if (nextPhase === 'exhale') playTone(392, 0.2);
          else playTone(440, 0.1);
          
          return selectedExercise[nextPhase];
        }
        return prev - 0.1;
      });
    }, 100);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, selectedExercise, currentPhase, currentCycle, getNextPhase, stopExercise, playTone]);

  // Animate circle based on phase
  useEffect(() => {
    if (!isRunning || !selectedExercise) return;
    
    const phaseDuration = selectedExercise[currentPhase];
    if (phaseDuration === 0) return;
    
    const progress = 1 - (timeLeft / phaseDuration);
    
    if (currentPhase === 'inhale') {
      setCircleScale(1 + progress * 0.5);
    } else if (currentPhase === 'exhale') {
      setCircleScale(1.5 - progress * 0.5);
    }
  }, [isRunning, selectedExercise, currentPhase, timeLeft]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Wind size={16} className="text-cyan-400" />
            Breathwork Exercises
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Guided breathing synced with your sounds
          </p>
        </div>
        {binauralActive && (
          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
            Synced with Binaural
          </span>
        )}
      </div>

      {/* Exercise Selection */}
      {!isRunning && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {breathworkExercises.map((exercise) => (
            <motion.button
              key={exercise.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedExercise(exercise)}
              data-testid={`breathwork-${exercise.id}`}
              className={cn(
                "p-4 rounded-xl border text-left transition-all duration-200",
                selectedExercise?.id === exercise.id
                  ? "border-cyan-500 bg-cyan-500/20"
                  : "border-white/10 bg-white/5 hover:border-white/20"
              )}
            >
              <span className="text-2xl mb-2 block">{exercise.icon}</span>
              <h5 className="text-sm font-medium text-white mb-1">{exercise.name}</h5>
              <p className="text-xs text-gray-500">{exercise.description}</p>
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                <Timer size={10} />
                <span>{exercise.cycles} cycles</span>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* Active Exercise Display */}
      <AnimatePresence mode="wait">
        {selectedExercise && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass-card p-6"
          >
            {isRunning ? (
              <div className="text-center">
                {/* Breathing Circle */}
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <motion.div
                    animate={{ scale: circleScale }}
                    transition={{ duration: 0.1, ease: "linear" }}
                    className="absolute inset-0 rounded-full border-4 flex items-center justify-center"
                    style={{ 
                      borderColor: selectedExercise.color,
                      boxShadow: `0 0 40px ${selectedExercise.color}40`
                    }}
                  >
                    <div className="text-center">
                      <div className="text-3xl font-bold text-white mb-1">
                        {Math.ceil(timeLeft)}
                      </div>
                      <div className="text-sm text-gray-400">
                        {phaseLabels[currentPhase]}
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Progress ring */}
                  <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="90"
                      fill="none"
                      stroke={selectedExercise.color}
                      strokeWidth="2"
                      strokeDasharray={565}
                      strokeDashoffset={565 * (timeLeft / selectedExercise[currentPhase])}
                      opacity="0.3"
                    />
                  </svg>
                </div>

                {/* Cycle Progress */}
                <div className="mb-4">
                  <span className="text-sm text-gray-400">
                    Cycle {currentCycle} of {selectedExercise.cycles}
                  </span>
                  <div className="flex justify-center gap-1 mt-2">
                    {Array.from({ length: selectedExercise.cycles }).map((_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          i < currentCycle ? "bg-cyan-500" : "bg-white/20"
                        )}
                      />
                    ))}
                  </div>
                </div>

                <NeonButton
                  variant="secondary"
                  onClick={stopExercise}
                  data-testid="breathwork-stop"
                >
                  <Pause size={18} className="mr-2" />
                  Stop
                </NeonButton>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{selectedExercise.icon}</span>
                  <div>
                    <h4 className="text-lg font-semibold text-white">{selectedExercise.name}</h4>
                    <p className="text-sm text-gray-400">{selectedExercise.description}</p>
                  </div>
                </div>

                {/* Pattern Display */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { label: "Inhale", value: selectedExercise.inhale },
                    { label: "Hold", value: selectedExercise.hold1 },
                    { label: "Exhale", value: selectedExercise.exhale },
                    { label: "Hold", value: selectedExercise.hold2 }
                  ].map((item, i) => (
                    <div key={i} className="text-center p-2 rounded-lg bg-white/5">
                      <div className="text-lg font-mono text-white">{item.value}s</div>
                      <div className="text-xs text-gray-500">{item.label}</div>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div className="mb-4">
                  <span className="text-xs text-gray-500">Benefits:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedExercise.benefits.map((benefit, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-full bg-white/5 text-gray-400">
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <NeonButton onClick={startExercise} data-testid="breathwork-start" className="flex-1">
                    <Play size={18} className="mr-2" />
                    Start ({selectedExercise.cycles} cycles)
                  </NeonButton>
                  <NeonButton 
                    variant="ghost" 
                    onClick={() => setSelectedExercise(null)}
                    data-testid="breathwork-back"
                  >
                    <RotateCcw size={18} />
                  </NeonButton>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Breathwork;
